import React, { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';

const AccountSearchHeroBox = ({
  searchQuery,
  setSearchQuery,
  onSearch,
  isLoading,
  onScrollToSettings,
  recentSearches = [],
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';
  const [avatarError, setAvatarError] = useState(false);

  const handleSubmit = useCallback((e) => {
    if (e) e.preventDefault();
    const trimmed = (searchQuery || '').trim();
    if (trimmed) {
      onSearch(trimmed);
    }
  }, [searchQuery, onSearch]);

  const handleClear = useCallback(() => {
    setSearchQuery('');
    setAvatarError(false);
  }, [setSearchQuery]);

  const handleSelectRecent = useCallback((nick) => {
    setSearchQuery(nick);
    setAvatarError(false);
    onSearch(nick);
  }, [setSearchQuery, onSearch]);

  const trimmedQuery = (searchQuery || '').trim();

  return (
    <section className="search-hero-box">
      <form className="username-input-capsule" onSubmit={handleSubmit}>
        <div className="search-field-row">
          {/* Аватарка или иконка игрока */}
          <div className="input-avatar-badge" title={isEn ? 'Player skin preview' : 'Предпросмотр скина игрока'}>
            {trimmedQuery && !avatarError ? (
              <img
                src={`https://mc-heads.net/avatar/${encodeURIComponent(trimmedQuery)}/38`}
                alt={trimmedQuery}
                onError={() => setAvatarError(true)}
              />
            ) : (
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="8" r="5" />
                <path d="M20 21a8 8 0 1 0-16 0" />
              </svg>
            )}
          </div>

          {/* Поле ввода ника */}
          <input
            type="text"
            className="username-field"
            placeholder={isEn
              ? 'Enter Minecraft username (e.g. Notch, Steve, Dream)...'
              : 'Введите имя пользователя Minecraft (напр. Notch, Steve, Dream)...'}
            autoComplete="off"
            spellCheck="false"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setAvatarError(false);
            }}
          />

          {/* Кнопка быстрой очистки поля */}
          {trimmedQuery.length > 0 && (
            <button
              type="button"
              className="clear-btn"
              style={{ display: 'inline-flex' }}
              onClick={handleClear}
              title={isEn ? 'Clear' : 'Очистить'}
            >
              <svg viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        <div className="search-actions-row">
          {/* Кнопка быстрого перехода к фильтрам */}
          <button
            type="button"
            className="quick-settings-trigger"
            onClick={onScrollToSettings}
            title={isEn ? 'Configure databases and filters' : 'Настроить базы и фильтры'}
          >
            <svg viewBox="0 0 24 24" fill="none">
              <line x1="4" y1="21" x2="4" y2="14" />
              <line x1="4" y1="10" x2="4" y2="3" />
              <line x1="12" y1="21" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12" y2="3" />
              <line x1="20" y1="21" x2="20" y2="16" />
              <line x1="20" y1="12" x2="20" y2="3" />
              <line x1="1" y1="14" x2="7" y2="14" />
              <line x1="9" y1="8" x2="15" y2="8" />
              <line x1="17" y1="16" x2="23" y2="16" />
            </svg>
            <span>{isEn ? 'Filters' : 'Фильтры'}</span>
          </button>

          {/* Главная кнопка поиска */}
          <button
            type="submit"
            className={`search-action-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="btn-spinner" />
                <span>{isEn ? 'Checking...' : 'Проверка...'}</span>
              </>
            ) : (
              <>
                <span>{isEn ? 'Check account' : 'Проверить аккаунт'}</span>
                <svg className="btn-arrow-icon" viewBox="0 0 24 24">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Недавние поиски из реальной истории */}
      {recentSearches && recentSearches.length > 0 && (
        <div className="recent-searches-bar">
          <div className="recent-label">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>{isEn ? 'Recent searches:' : 'Недавние поиски:'}</span>
          </div>
          <div className="recent-chips-group">
            <AnimatePresence initial={false}>
              {recentSearches.slice(0, 10).map((item) => {
                const name = item.name || item.username;
                const count = item.count !== undefined ? item.count : (item.results !== undefined ? item.results : 0);
                const safeKey = (name || '').toLowerCase();
                return (
                  <motion.button
                    key={safeKey}
                    layout
                    initial={{ opacity: 0, x: -32, scale: 0.88 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.85, transition: { duration: 0.2 } }}
                    transition={{
                      opacity: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
                      x: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
                      scale: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
                      layout: { duration: 0.4, ease: [0.16, 1, 0.3, 1] }
                    }}
                    type="button"
                    className="recent-chip"
                    onClick={() => handleSelectRecent(name)}
                  >
                    <span>{name}</span>
                    <span className="chip-badge">
                      {count} {isEn ? 'finds' : (count % 10 === 1 && count !== 11 ? 'находка' : (count % 10 >= 2 && count % 10 <= 4 ? 'находки' : 'находок'))}
                    </span>
                  </motion.button>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      )}
    </section>
  );
};

export default AccountSearchHeroBox;
